import React, { useRef } from "react";
import { motion, useInView, useAnimation } from "framer-motion";
import { Link } from "react-router-dom";
import HeroSection from "../../components/HeroSection";
import { FaArrowRight, FaCheckCircle, FaUserTie, FaGraduationCap, FaLightbulb } from "react-icons/fa";
import classStudents from "../../assets/gallery/education/classstudents.png";
import scholarship from "../../assets/gallery/education/scholarship.jpeg";
import techTraining1 from "../../assets/gallery/education/tech-training1.jpg";
import { getCollageImages } from "../../utils/heroImages";

const AnimatedSection = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  if (isInView) {
    controls.start("visible");
  }

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 }
      }}
      transition={{ duration: 0.6, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default function MentorshipProgram() {
  const mentorshipTypes = [
    {
      icon: <FaUserTie className="text-3xl text-primary-600" />,
      title: "Career Mentorship",
      description: "One-on-one guidance from experienced professionals in your field to help navigate career decisions and advancement opportunities."
    },
    {
      icon: <FaGraduationCap className="text-3xl text-primary-600" />,
      title: "Academic Mentorship",
      description: "Support for students navigating academic challenges, course selection, and educational planning."
    },
    {
      icon: <FaLightbulb className="text-3xl text-primary-600" />,
      title: "Entrepreneurship Mentorship",
      description: "Guidance for aspiring entrepreneurs on starting and growing businesses, from ideation to execution."
    }
  ];

  const benefits = [
    "Personalized guidance from experienced professionals",
    "Networking opportunities within your industry",
    "Career advancement support and advice",
    "Access to professional resources and tools",
    "Regular check-ins and progress tracking",
    "Long-term professional relationships"
  ];

  return (
    <div className="w-full bg-white">
      <HeroSection
        title="Mentorship Program"
        subtitle="Connecting community members with experienced mentors who provide guidance, support, and professional development opportunities."
        buttons={[
          { text: "Apply Now", link: "/register", variant: "blue" },
          { text: "Login", link: "/login", variant: "blue" }
        ]}
        collageImages={getCollageImages("mentorship")}
      />

      {/* Introduction */}
      <section className="section bg-white">
        <div className="section-container">
          <AnimatedSection>
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
                Guidance That Transforms Careers
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our Mentorship Program pairs community members with experienced professionals who provide 
                personalized guidance, career advice, and professional development support. Whether you're 
                a student, early-career professional, or aspiring entrepreneur, our mentors can help you achieve your goals.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Mentorship Types */}
      <section className="section bg-gray-50">
        <div className="section-container">
          <div className="grid md:grid-cols-3 gap-6">
            {mentorshipTypes.map((type, index) => (
              <AnimatedSection key={index} delay={0.2 + index * 0.1}>
                <div className="card text-center">
                  <div className="flex justify-center mb-4">
                    {type.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">
                    {type.title}
                  </h3>
                  <p className="text-gray-600">
                    {type.description}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section bg-white">
        <div className="section-container">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Program Benefits
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {benefits.map((benefit, index) => (
              <AnimatedSection key={index} delay={0.2 + index * 0.1}>
                <div className="card">
                  <div className="flex items-start gap-3">
                    <FaCheckCircle className="text-primary-600 text-xl flex-shrink-0 mt-1" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="section text-center"
        style={{
          backgroundColor: '#F9FAFB'
        }}
      >
        <div className="section-container">
          <AnimatedSection>
            <h2 
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 700,
                color: '#1F2937'
              }}
            >
              Get Started Today
            </h2>
            <p 
              className="text-lg max-w-2xl mx-auto mb-8"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 400,
                color: '#374151',
                lineHeight: 1.6
              }}
            >
              Whether you're looking for a mentor or want to become one, we can help you get started.
            </p>
            <Link 
              to="/register" 
              className="inline-flex items-center justify-center gap-2 px-8 py-3 font-semibold rounded-md transition-colors"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 600,
                backgroundColor: '#1E40AF',
                color: '#FFFFFF',
                textDecoration: 'none',
                borderRadius: '6px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#1E3A8A';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#1E40AF';
              }}
            >
              Apply Now
              <FaArrowRight />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
